/**
 * API mijozi. Bitta origin: sessiya cookie + CSRF sarlavhasi.
 * Xato formati backend bilan bir xil: {error: {code, message, fields?, current?}}.
 * X-Language: ekranda ko'rinib turgan til -- server xabarlari ham shu tilda keladi.
 */
import { useRef } from "react";
import i18n from "i18next";

export interface ApiErrorBody {
  code: string;
  message: string;
  fields?: Record<string, string[]>;
  current?: unknown;
  changed_by?: string | null;
}

export class ApiError extends Error {
  status: number;
  code: string;
  fields?: Record<string, string[]>;
  current?: unknown;
  changedBy?: string | null;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code;
    this.fields = body.fields;
    this.current = body.current;
    this.changedBy = body.changed_by;
  }

  get isConflict() {
    return this.status === 409;
  }
}

export function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

export interface RequestOptions {
  idempotencyKey?: string;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined | null | Array<string | number>>;
  formData?: FormData;
}

export function buildQuery(query?: RequestOptions["query"]): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) value.forEach((v) => params.append(key, String(v)));
    else params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

let unauthorizedHandler: (() => void) | null = null;
export function onUnauthorized(handler: () => void) {
  unauthorizedHandler = handler;
}

// 2FA siyosati: majburiy roldagi xodim 2FA'ni sozlamagan (sozlama ochiq oynada o'zgargan bo'lishi mumkin)
let twofaSetupHandler: (() => void) | null = null;
export function onTwofaSetupRequired(handler: () => void) {
  twofaSetupHandler = handler;
}

// Vaqtinchalik parol: owner/menejer yangi parol berdi -- xodim o'zinikini qo'ymaguncha platforma yopiq
let passwordChangeHandler: (() => void) | null = null;
export function onPasswordChangeRequired(handler: () => void) {
  passwordChangeHandler = handler;
}

async function request<T>(method: string, url: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const lang = i18n.resolvedLanguage;
  if (lang) headers["X-Language"] = lang;
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = getCookie("csrftoken");
    if (csrf) headers["X-CSRFToken"] = csrf;
  }
  if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;
  let payload: BodyInit | undefined;
  if (opts.formData) {
    payload = opts.formData;
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  const res = await fetch(url + buildQuery(opts.query), {
    method,
    headers,
    body: payload,
    credentials: "same-origin",
    signal: opts.signal,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const err = (data as { error?: ApiErrorBody } | null)?.error ?? {
      code: res.status === 403 ? "forbidden" : "error",
      message: res.statusText || "Request failed",
    };
    // Bloklangan foydalanuvchi: sessiya serverda yopiladi -- login sahifasiga (2026-10-18)
    if (res.status === 401 || (res.status === 403 && (err.code === "not_authenticated" || err.code === "blocked"))) {
      unauthorizedHandler?.();
    }
    if (res.status === 403 && err.code === "2fa_setup_required") {
      twofaSetupHandler?.();
    }
    if (res.status === 403 && err.code === "password_change_required") {
      passwordChangeHandler?.();
    }
    throw new ApiError(res.status, err);
  }
  return data as T;
}

export const api = {
  get: <T>(url: string, opts?: RequestOptions) => request<T>("GET", url, undefined, opts),
  post: <T>(url: string, body?: unknown, opts?: RequestOptions) => request<T>("POST", url, body, opts),
  patch: <T>(url: string, body?: unknown, opts?: RequestOptions) => request<T>("PATCH", url, body, opts),
  put: <T>(url: string, body?: unknown, opts?: RequestOptions) => request<T>("PUT", url, body, opts),
  delete: <T>(url: string, opts?: RequestOptions) => request<T>("DELETE", url, undefined, opts),
};

export interface Paginated<T> {
  count: number;
  page: number;
  page_size: number;
  pages: number;
  results: T[];
  /** Qidiruv aniq topmadi -- natija o'xshashlar (backend core.textsearch) */
  fuzzy?: boolean;
}

/**
 * Ro'yxatning HAMMA sahifalari bitta javobda (backend bir sahifada 100 tadan ko'p bermaydi). Tanlov ro'yxatlari, kichik to'plamlar va
 * brauzerda sanaladigan/filtrlanadigan ro'yxatlar uchun: `page_size: 100` bilan bitta so'rov ortig'ini jim kesib qo'yardi (2026-10-25).
 * Katta ro'yxatlar ko'rinishda -- server sahifalash (`Pagination`), jami summa/son -- server'da.
 */
export async function getAllPages<T>(url: string, query: RequestOptions["query"] = {}): Promise<Paginated<T>> {
  const results: T[] = [];
  for (let page = 1; ; page++) {
    const res = await api.get<Paginated<T>>(url, { query: { ...query, page, page_size: 100 } });
    results.push(...res.results);
    if (page >= res.pages || res.results.length === 0) return { count: res.count, page: 1, page_size: results.length, pages: 1, results };
  }
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** Bir xil so'rov qayta yuborilsa (tarmoq uzilishi, ikki bosish) server uni bir marta bajaradi: kalit payload bo'yicha
 * beriladi va muvaffaqiyatdan keyin tozalanadi -- keyingi bir xil yozuv (haqiqiy ikkinchisi) yangi kalit oladi. */
export function useIdempotencyKeys() {
  const keys = useRef(new Map<string, string>()).current;
  return {
    for(payload: unknown): string {
      const sig = JSON.stringify(payload);
      let key = keys.get(sig);
      if (!key) {
        key = newIdempotencyKey();
        keys.set(sig, key);
      }
      return key;
    },
    done(payload: unknown) {
      keys.delete(JSON.stringify(payload));
    },
  };
}
